import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { emailMask } from 'text-mask-addons';
import PropTypes from 'prop-types';
import { forEach, get } from 'lodash';

import { FormRow, Input, AddressInput } from 'components/Form';
import Validator from 'components/Validator';
import { Button } from 'components/Button';
import { User } from 'components/Icons';
import GeneralError from 'components/GeneralError';
import { dateMask, numberUnmask, phoneMask, ssnMask } from 'utils/masks';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { MISMATCH_TITLES } from 'utils/constants';
import { nextAction } from 'actions/workflow';

import RepromptingSchema from './schema';

const Reprompting = ({
  navigate,
  dispatch,
  searchParams,
  validator: { values, setValues, errors, validate, onChangeHandler },
}) => {
  useWorkflowGuard(searchParams, navigate);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const workflow = useSelector((state) => state.workflow);
  const mismatches = get(workflow, 'data.mismatches', []);

  useEffect(() => {
    if (mismatches && mismatches.length > 0) {
      setValues({ ...values, mismatches });
    }
  }, [mismatches]);

  const isFormComplete = useMemo(() => {
    return mismatches.every((field) => {
      if (field === 'Address') {
        return !!(values.streetAddress && values.city && values.state && values.zip);
      }
      return !!values[field];
    });
  }, [mismatches, values]);

  const addressInitialValues = useMemo(() => ({
      streetAddress: values.streetAddress,
      zip: values.zip,
      state: values.state,
      city: values.city,
    }), [values.streetAddress, values.zip, values.state, values.city]);

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const onAddressSave = (addressValues) => {
    setValues({
      ...values,
      ...addressValues,
    })
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setGeneralError(null);

    if (!validate().isValid) {
      return;
    }

    setIsSubmitting(true);
    const data = {};
    forEach(mismatches, (field) => {
      if (field === 'Address') {
        data.Address = {
          StreetAddress: get(values, 'streetAddress'),
          City: get(values, 'city'),
          State: get(values, 'state.value'),
          ZipCode: numberUnmask(get(values, 'zip')),
        };
      } else if (field === 'Ssn' || field === 'MobileNumber') {
        data[field] = numberUnmask(get(values, field));
      } else {
        data[field] = get(values, field);
      }
    });

    dispatch(nextAction({
      key: searchParams.get('key'),
      data,
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: (err) => {
        setIsSubmitting(false);
        setGeneralError(get(err, 'data.failure.errors.0') || { message: 'Something went wrong' });
      }
    }));
  };

  const renderMismatchField = (field) => {
    switch (field) {
      case 'LastName':
        return (
          <Input
            placeholder="Last Name"
            value={values.LastName}
            error={errors.LastName}
            onChange={(e) => handleChange('LastName', e.target.value)}
            required
          />
        );
      case 'Ssn':
        return (
          <Input
            inputClassName="font-numeric"
            value={values.Ssn}
            error={errors.Ssn}
            onChange={(e) => handleChange('Ssn', e.target.value)}
            mask={ssnMask}
            placeholder="___-__-____"
            required
            inputMode="numeric"
          />
        );
      case 'MobileNumber':
        return (
          <Input
            inputClassName="font-numeric"
            placeholder="(+1)"
            mask={phoneMask}
            value={values.MobileNumber}
            error={errors.MobileNumber}
            onChange={(e) => handleChange('MobileNumber', e.target.value)}
            required
            inputMode="numeric"
          />
        );
      case 'Email':
        return (
          <Input
            placeholder="e.g. email@company.com"
            mask={emailMask}
            value={values.Email}
            error={errors.Email}
            onChange={(e) => handleChange('Email', e.target.value)}
            required
          />
        );
      case 'DateOfBirth':
        return (
          <Input
            placeholder="MM/DD/YYYY"
            inputClassName="font-numeric"
            mask={dateMask}
            value={values.DateOfBirth}
            error={errors.DateOfBirth}
            onChange={(e) => handleChange('DateOfBirth', e.target.value)}
            required
            inputMode="numeric"
          />
        );
      case 'Address':
        return (
          <AddressInput
            required
            initialValues={addressInitialValues}
            onSave={onAddressSave}
            error={errors.Address}
          />
        );
      default:
        return null;
    }
  };

  const renderCard = (field, count) => (
    <div className="h-full border border-slate-200 px-4 py-6 rounded-lg relative">
      <div className="flex items-center justify-center text-blue-500 text-sm font-semibold absolute w-8 h-8 bg-sky-100 rounded-full -top-4 left-1/2 transform -translate-x-1/2">
        {count}
      </div>
      <span className="text-center text-sm text-slate-800 font-semibold mb-4 mt-1/2 block">{MISMATCH_TITLES[field] || field}</span>
      <div>
        {renderMismatchField(field)}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col items-center mb-8">
      <User size={72} className="mb-4" />

      <p className="text-xl md:text-3xl font-semibold text-slate-800 mb-6 text-center">
        We had a difficult<br className="hidden md:block" /> time verifying you
      </p>

      <p className="text-sm md:text-base text-slate-600 mb-10 text-center">
        We found {mismatches.length} discrepanc{mismatches.length !== 1 ? 'ies' : 'y'}. Please review and confirm.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 gap-x-3 mb-8 w-full">
        {mismatches.map((field, index) => (
          <div key={index} className={`${field === 'Address' ? 'md:col-span-2' : ''} ${mismatches.length === 1 ? 'md:col-start-2' : ''}`}>
            {renderCard(field, index + 1)}
          </div>
        ))}
      </div>

      <GeneralError error={generalError} className="mb-4" />

      <FormRow className="w-full">
        <Button
          className="w-2/3 mx-auto"
          label="SUBMIT"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!isFormComplete}
        />
      </FormRow>
    </div>
  );
};

Reprompting.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Validator(RepromptingSchema)(Reprompting);
